import { memo, ReactNode, useState, useRef, MutableRefObject } from 'react'
import { Link, Redirect } from 'react-router-dom'
import classnames from 'classnames'
import { Logo } from '@campgladiator/cgui-core.atoms.logo'
import { Card } from '@campgladiator/cgui-core.atoms.card'
import { Paragraph } from '@campgladiator/cgui-core.atoms.typography'
import { Button } from '@campgladiator/cgui-core.atoms.button'
import { Avatar } from '@campgladiator/cgui-core.atoms.avatar'
import { Icon } from '@campgladiator/cgui-core.atoms.icon'
import {
  ariaLabels,
  ariaLabelAttribute,
  testIds,
  testIdAttribute,
} from '../../../common/utility/labels'
import { logout } from '../../../redux/features/Auth/'
import { useAppDispatch } from '../../../redux/store'
import { HeaderLinks, CartLink } from './HeaderLinks'
import { useDeviceWidth, useOuterClick } from '../../hooks'
import styles from './Header.module.scss'

const navCollapseWidth = 1200
const logoSize = '38px'

type HeaderProps = {
  isLoggedIn: boolean
  user?: { image_url?: string }
}

type MenuNavProps = {
  collapsed: boolean
  children?: ReactNode
}

type MainMenuProps = DisplayLoginOrSignUpProps & {
  isLoggedIn: boolean
  toggled: boolean
  deviceWidth: number
}

type DisplayProfileOrLoginProps = DisplayLoggedInProfileProps &
  DisplayLoginOrSignUpProps & {
    isLoggedIn: boolean
    deviceWidth: number
  }

type DisplayLoginOrSignUpProps = {
  onLoginClick?: () => void
}

type DisplayLoggedInProfileProps = {
  userImage?: string
  userMenuToggled: boolean
  onUserMenuToggle: () => void
  userMenuRef: MutableRefObject<HTMLDivElement | null>
}

type MenuIconProps = {
  toggled: boolean
  handleToggled: () => void
  menuIconRef: MutableRefObject<HTMLButtonElement | null>
}

const Cart = () => (
  <a className={classnames(styles.item, styles.cartButton)} href={CartLink}>
    <Icon name={'icon-shopping-cart-line'} />
  </a>
)

const UserMenu = () => (
  <Card raised className={classnames(styles.card, styles.userMenu)}>
    <Logout />
  </Card>
)

const DisplayLoggedInProfile = ({
  userImage = '',
  userMenuToggled = false,
  onUserMenuToggle = () => {},
  userMenuRef,
}: DisplayLoggedInProfileProps) => {
  const ariaLabel = ariaLabelAttribute(
    ariaLabels.HEADER.PROFILE_PICTURE_ICON_BUTTON,
  )
  const testId = testIdAttribute(testIds.HEADER.PROFILE_PICTURE_ICON_BUTTON)

  return (
    <>
      <div
        className={styles.avatarContainer}
        onClick={onUserMenuToggle}
        ref={userMenuRef}
        {...ariaLabel}
        {...testId}
      >
        {!!userImage ? (
          <Avatar size="30px" src={userImage} />
        ) : (
          <Avatar size="30px" />
        )}
        <Icon
          name={userMenuToggled ? 'icon-angle-up-line' : 'icon-angle-down-line'}
        />
      </div>
      {userMenuToggled && <UserMenu />}
    </>
  )
}

const DisplayLoginOrSignUp = ({
  onLoginClick = () => {},
}: DisplayLoginOrSignUpProps) => (
  <Button emphasis="secondary" size="small" onClick={onLoginClick}>
    Login or sign up
  </Button>
)

const MenuNav = ({ collapsed = true, children }: MenuNavProps) => (
  <ul className={classnames(styles.list, { [styles.collapsed]: collapsed })}>
    {HeaderLinks.map((nav, index) => (
      <li
        key={index}
        className={classnames(styles.item, {
          [styles.active]: nav.active,
        })}
      >
        <a
          className={classnames(styles.link, {
            [styles.active]: nav.active,
          })}
          href={nav.href}
        >
          <Paragraph size="small" weight={nav.active ? 'normal' : 'book'}>
            {nav.name}
          </Paragraph>
        </a>
      </li>
    ))}
    {children}
  </ul>
)

const MainMenu = ({
  onLoginClick = () => {},
  isLoggedIn = false,
  deviceWidth = 0,
  toggled = false,
}: MainMenuProps) => {
  const mobile = deviceWidth < navCollapseWidth && toggled
  const desktop = deviceWidth >= navCollapseWidth
  return (
    <nav className={styles.nav}>
      {(mobile && (
        <Card raised className={styles.card}>
          <MenuNav collapsed={true}>
            {!isLoggedIn && (
              <li className={classnames(styles.item)}>
                <button
                  className={classnames(styles.link, styles.transparentButton)}
                  onClick={onLoginClick}
                >
                  <Paragraph size="small" weight="book">
                    Login or Sign Up
                  </Paragraph>
                </button>
              </li>
            )}
          </MenuNav>
        </Card>
      )) ||
        (desktop && <MenuNav collapsed={false} />)}
    </nav>
  )
}

const MenuIcon = ({
  toggled = false,
  handleToggled = () => {},
  menuIconRef,
}: MenuIconProps) => (
  <button
    className={classnames(styles.item, styles.menuButton)}
    onClick={handleToggled}
    ref={menuIconRef}
  >
    <Icon name={toggled ? 'icon-times-line' : 'icon-bars-line'} />
  </button>
)

const DisplayProfileOrLoginBtn = ({
  userImage = '',
  onLoginClick,
  isLoggedIn,
  deviceWidth = 0,
  userMenuToggled,
  onUserMenuToggle,
  userMenuRef,
}: DisplayProfileOrLoginProps) => (
  <div className={classnames(styles.item, styles.headerButton)}>
    {isLoggedIn ? (
      <DisplayLoggedInProfile
        userImage={userImage}
        userMenuToggled={userMenuToggled}
        onUserMenuToggle={onUserMenuToggle}
        userMenuRef={userMenuRef}
      />
    ) : (
      deviceWidth >= navCollapseWidth && (
        <DisplayLoginOrSignUp onLoginClick={onLoginClick} />
      )
    )}
  </div>
)

const Logout = () => {
  const dispatch = useAppDispatch()

  const handleLogout = () => {
    dispatch(logout())
  }

  return (
    <button
      className={classnames(styles.link, styles.transparentButton)}
      onClick={handleLogout}
    >
      <Paragraph size="small" weight="book">
        Log out
      </Paragraph>
    </button>
  )
}

const Header = ({ isLoggedIn, user }: HeaderProps) => {
  const [isLoginClicked, setIsLoginClicked] = useState(false)
  const [mainMenuToggled, setMainMenuToggled] = useState(false)
  const [userMenuToggled, setUserMenuToggled] = useState(false)
  const userImage = user?.image_url
  const userMenuRef = useRef<HTMLDivElement>(null)
  const menuIconRef = useRef<HTMLButtonElement>(null)
  const deviceWidth = useDeviceWidth()

  const handleToggleMainMenu = () => setMainMenuToggled(!mainMenuToggled)
  const handleToggleUserMenu = () => setUserMenuToggled(!userMenuToggled)
  const handleLoginClick = () => setIsLoginClicked(true)

  useOuterClick(userMenuRef, () => setUserMenuToggled(false))
  useOuterClick(menuIconRef, () => setMainMenuToggled(false))

  return isLoginClicked ? (
    <Redirect push to="/login" />
  ) : (
    <header className={styles.header}>
      <div
        className={styles.logoWrapper}
        style={{ minWidth: logoSize, minHeight: logoSize }}
      >
        <Link className={styles.logo} to="/">
          <Logo type="icon" iconType="red" width={logoSize} />
        </Link>
      </div>
      <MainMenu
        deviceWidth={deviceWidth}
        toggled={mainMenuToggled}
        isLoggedIn={isLoggedIn}
        onLoginClick={handleLoginClick}
      />
      <Cart />
      <DisplayProfileOrLoginBtn
        userImage={userImage}
        isLoggedIn={isLoggedIn}
        deviceWidth={deviceWidth}
        userMenuToggled={userMenuToggled}
        onUserMenuToggle={handleToggleUserMenu}
        userMenuRef={userMenuRef}
        onLoginClick={handleLoginClick}
      />
      {deviceWidth < navCollapseWidth ? (
        <MenuIcon
          toggled={mainMenuToggled}
          handleToggled={handleToggleMainMenu}
          menuIconRef={menuIconRef}
        />
      ) : null}
    </header>
  )
}

export default memo(Header)
